import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, QrCode } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import StaffLiveTrip from "@/components/StaffLiveTrip";
import { Button, IconButton } from "@/components/ui-kit/Button";
import { fmtYmd } from "@/lib/dateFormat";
import DatePickerYmd from "@/components/DatePickerYmd";
import QrScanner from "@/components/QrScanner";

const emptyForm = {
    id: null,
    vehicle_id: "",
    operation_date: new Date().toISOString().slice(0, 10),
    start_time: "",
    end_time: "",
    start_odometer_km: "",
    end_odometer_km: "",
    origin: "",
    destination: "",
    purpose: "",
    kind: "business",
    passenger_count: "",
    fuel_added_l: "",
    notes: "",
};

export default function Operations() {
    const { can, user } = useAuth();
    const canCreate = can("operations.create");
    const canUpdate = can("operations.update");
    const canDelete = can("operations.delete");
    // Staff still get a confirmation step before submit, but now they can also
    // browse their own past records (backend scopes /operations to the
    // logged-in driver_id when role === "staff").
    const isStaffView = user?.role === "staff";

    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [kinds, setKinds] = useState([]);  // from /api/operation-kinds
    const [locations, setLocations] = useState([]);  // from /api/taxonomies/operation_location
    const [filterVehicle, setFilterVehicle] = useState("");
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");

    const [open, setOpen] = useState(false);
    // Staff flow: after the staff hits 「登録」 we switch the dialog into a
    // read-only confirmation step so they can visually verify every entered
    // value before the record is persisted. Admins bypass this and submit
    // directly.
    const [confirming, setConfirming] = useState(false);
    const [form, setForm] = useState(emptyForm);
    // Latest end_odometer for the currently-selected vehicle (auto-filled into
    // start_odometer_km when the vehicle changes on a fresh form).
    const [lastOdo, setLastOdo] = useState(null);

    // When the user picks a vehicle on a NEW record, fetch the last
    // end_odometer_km. We auto-fill start_odometer_km when:
    //   • it's currently empty, OR
    //   • the current value is exactly the previous vehicle's auto-filled
    //     reading (so switching vehicles updates the value rather than
    //     stranding the prior auto-fill).
    // We never overwrite a value the user typed manually.
    useEffect(() => {
        if (!open || form.id) { setLastOdo(null); return; }
        if (!form.vehicle_id) { setLastOdo(null); return; }
        let cancelled = false;
        const previousAutoFill = lastOdo?.last_end_odometer_km;
        (async () => {
            try {
                const { data } = await api.get(`/operations/last-odometer/${form.vehicle_id}`);
                if (cancelled) return;
                setLastOdo(data);
                const newOdo = data?.last_end_odometer_km;
                setForm((f) => {
                    const cur = f.start_odometer_km;
                    const isEmpty = cur === "" || cur == null;
                    const isStaleAutoFill = previousAutoFill != null && String(cur) === String(previousAutoFill);
                    if (newOdo != null) {
                        if (isEmpty || isStaleAutoFill) {
                            return { ...f, start_odometer_km: String(newOdo) };
                        }
                    } else if (isStaleAutoFill) {
                        // New vehicle has no history → clear the prior auto-fill
                        return { ...f, start_odometer_km: "" };
                    }
                    return f;
                });
            } catch (e) {
                if (!cancelled) setLastOdo(null);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.vehicle_id, open, form.id]);

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (filterVehicle) params.set("vehicle_id", filterVehicle);
            if (filterFrom) params.set("date_from", filterFrom);
            if (filterTo) params.set("date_to", filterTo);
            const qs = params.toString();
            const [o, v, k, loc] = await Promise.all([
                api.get(`/operations${qs ? `?${qs}` : ""}`),
                api.get("/vehicles"),
                api.get("/operation-kinds"),
                api.get("/taxonomies/operation_location"),
            ]);
            setList(o.data);
            setVehicles(v.data);
            setKinds(k.data);
            setLocations(loc.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "読込失敗");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterVehicle, filterFrom, filterTo]);

    // Lookup helpers: value → { label, color }. Falls back to the raw value
    // if a record references a kind that was later deleted/disabled.
    const kindLookup = (val) => kinds.find((k) => k.value === val) || { label: val, color: "#888" };
    const enabledKinds = kinds.filter((k) => k.enabled);
    const enabledLocations = locations.filter((l) => l.enabled);

    // Preview distance from current odometer inputs
    const previewDistance = useMemo(() => {
        const s = parseInt(form.start_odometer_km);
        const e = parseInt(form.end_odometer_km);
        if (Number.isFinite(s) && Number.isFinite(e) && e >= s) return e - s;
        return null;
    }, [form.start_odometer_km, form.end_odometer_km]);

    // For a new record: open the QR scanner first — vehicle must be picked
    // by scanning its QR sticker. After a successful scan the form dialog
    // opens with the vehicle already selected.
    const [qrScanOpen, setQrScanOpen] = useState(false);

    const openNew = () => {
        setForm(emptyForm);
        setConfirming(false);
        setLastOdo(null);
        setQrScanOpen(true);
    };

    const handleQrScan = async (code) => {
        try {
            const { data } = await api.post("/vehicles/resolve-qr", { code });
            toast.success(`車両を選択: ${data.plate_number}`);
            setForm({ ...emptyForm, vehicle_id: data.id });
            setQrScanOpen(false);
            setOpen(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "QRコードを認識できません");
        }
    };

    const openEdit = (o) => {
        setForm({
            id: o.id,
            vehicle_id: o.vehicle_id,
            operation_date: o.operation_date,
            start_time: o.start_time || "",
            end_time: o.end_time || "",
            start_odometer_km: o.start_odometer_km ?? "",
            end_odometer_km: o.end_odometer_km ?? "",
            origin: o.origin || "",
            destination: o.destination || "",
            purpose: o.purpose || "",
            kind: o.kind || "business",
            passenger_count: o.passenger_count ?? "",
            fuel_added_l: o.fuel_added_l ?? "",
            notes: o.notes || "",
        });
        setConfirming(false);
        setOpen(true);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        // Staff gets a confirmation step; admins save directly (existing UX).
        if (isStaffView) {
            if (!form.vehicle_id || !form.operation_date) {
                toast.error("車両と運行日は必須です");
                return;
            }
            setConfirming(true);
        } else {
            doSubmit();
        }
    };

    const doSubmit = async () => {
        const payload = { ...form };
        // Convert numeric strings
        for (const k of ["start_odometer_km", "end_odometer_km", "passenger_count"]) {
            payload[k] = payload[k] === "" || payload[k] == null ? null : parseInt(payload[k]);
        }
        payload.fuel_added_l = payload.fuel_added_l === "" || payload.fuel_added_l == null
            ? null : parseFloat(payload.fuel_added_l);
        // Strip empty strings → null so PATCH doesn't wipe values
        for (const k of Object.keys(payload)) {
            if (payload[k] === "") payload[k] = null;
        }
        const editingId = payload.id;
        delete payload.id;
        try {
            if (editingId) {
                await api.patch(`/operations/${editingId}`, payload);
                toast.success("運行記録を更新しました");
            } else {
                const { data } = await api.post("/operations", payload);
                toast.success("運行記録を登録しました");
                // Surface any mileage alerts that fired from this reading
                const alerts = data?._triggered_alerts || [];
                for (const a of alerts) {
                    if (a.status === "overdue") {
                        toast.error(`⚠ ${a.title}`, { duration: 8000 });
                    } else {
                        toast.warning(`⚠ ${a.title}`, { duration: 6000 });
                    }
                }
            }
            setOpen(false);
            setConfirming(false);
            setForm(emptyForm);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "登録失敗");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("この運行記録を削除しますか？")) return;
        try {
            await api.delete(`/operations/${id}`);
            toast.success("削除しました");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "削除失敗");
        }
    };

    // Aggregate totals for the currently filtered view
    const totals = useMemo(() => ({
        trips: list.length,
        km: list.reduce((sum, o) => sum + (o.distance_km || 0), 0),
        fuel: list.reduce((sum, o) => sum + (o.fuel_added_l || 0), 0),
    }), [list]);

    // ---- Staff-only UX enhancements ----
    // Group the list into human-friendly buckets (今日 / 昨日 / 今週 / 先週 /
    // 2026年4月 …). Admins still see a flat list below.
    const groupedList = useMemo(() => {
        if (!isStaffView || list.length === 0) return null;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const dayMs = 86400000;
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const todayStr = fmt(today);
        const yesterdayStr = fmt(new Date(today.getTime() - dayMs));
        const sevenAgo = new Date(today.getTime() - 7 * dayMs);
        const fourteenAgo = new Date(today.getTime() - 14 * dayMs);

        const buckets = new Map();
        const order = [];
        const push = (key, item) => {
            if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
            buckets.get(key).push(item);
        };
        for (const o of list) {
            const d = o.operation_date;
            if (!d) { push("日付なし", o); continue; }
            const [y, m] = d.split("-");
            const dDate = new Date(d + "T00:00:00");
            if (d === todayStr) push("今日", o);
            else if (d === yesterdayStr) push("昨日", o);
            else if (dDate >= sevenAgo) push("今週", o);
            else if (dDate >= fourteenAgo) push("先週", o);
            else push(`${y}年${parseInt(m, 10)}月`, o);
        }
        return order.map((key) => ({ key, items: buckets.get(key) }));
    }, [list, isStaffView]);

    // Preset period chips (今日 / 今週 / 今月 / 直近30日 / 全期間)
    const applyPreset = (preset) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        if (preset === "today") {
            setFilterFrom(fmt(today)); setFilterTo(fmt(today));
        } else if (preset === "week") {
            const dow = today.getDay() || 7;
            const mon = new Date(today); mon.setDate(today.getDate() - (dow - 1));
            setFilterFrom(fmt(mon)); setFilterTo(fmt(today));
        } else if (preset === "month") {
            setFilterFrom(fmt(new Date(today.getFullYear(), today.getMonth(), 1)));
            setFilterTo(fmt(today));
        } else if (preset === "30d") {
            setFilterFrom(fmt(new Date(today.getTime() - 30 * 86400000)));
            setFilterTo(fmt(today));
        } else {
            setFilterFrom(""); setFilterTo("");
        }
    };
    // Which preset button is "active" — matches current filterFrom/filterTo
    const activePreset = useMemo(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        if (!filterFrom && !filterTo) return "all";
        if (filterFrom === fmt(today) && filterTo === fmt(today)) return "today";
        const dow = today.getDay() || 7;
        const mon = new Date(today); mon.setDate(today.getDate() - (dow - 1));
        if (filterFrom === fmt(mon) && filterTo === fmt(today)) return "week";
        if (filterFrom === fmt(new Date(today.getFullYear(), today.getMonth(), 1)) && filterTo === fmt(today)) return "month";
        if (filterFrom === fmt(new Date(today.getTime() - 30 * 86400000)) && filterTo === fmt(today)) return "30d";
        return "custom";
    }, [filterFrom, filterTo]);

    return (
        <div className="space-y-4 sm:space-y-5" data-testid="operations-page">
            <div className="flex justify-between items-end flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">// 運行記録 / OPERATION JOURNAL</p>
                    <h1 className="text-xl sm:text-[1.685rem] font-bold tracking-tight">
                        {isStaffView ? "自分の運行記録" : "運行日報一覧"}
                    </h1>
                </div>
                {canCreate && !isStaffView && (
                    <Button leftIcon={Plus} onClick={openNew} data-testid="op-new-btn">運行を記録</Button>
                )}
            </div>

            {/* Staff: 2-step ride flow (replaces the legacy one-shot form). */}
            {isStaffView && canCreate && (
                <StaffLiveTrip
                    vehicles={vehicles}
                    locations={locations}
                    kinds={kinds}
                    onTripFinished={load}
                />
            )}

            {/* KPI strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatBox label={isStaffView ? "自分の記録数" : "記録数"} value={`${totals.trips} 件`} testId="op-stat-trips" />
                <StatBox label="走行距離合計" value={`${totals.km.toLocaleString()} km`} testId="op-stat-km" />
                <StatBox label="給油合計" value={`${totals.fuel.toFixed(1)} L`} testId="op-stat-fuel" />
            </div>

            {/* Preset period chips — staff only (admin can use the date pickers) */}
            {isStaffView && (
                <div className="flex flex-wrap gap-1.5" data-testid="op-preset-chips">
                    {[
                        { key: "today", label: "今日" },
                        { key: "week", label: "今週" },
                        { key: "month", label: "今月" },
                        { key: "30d", label: "直近30日" },
                        { key: "all", label: "全期間" },
                    ].map((p) => {
                        const active = activePreset === p.key;
                        return (
                            <button
                                key={p.key}
                                type="button"
                                onClick={() => applyPreset(p.key)}
                                className={
                                    "px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm border transition " +
                                    (active
                                        ? "bg-[var(--prc-green)] text-black border-[var(--prc-green)]"
                                        : "bg-[var(--prc-card)] text-[var(--prc-muted)] border-[var(--prc-border)] hover:text-[var(--prc-green)] hover:border-[var(--prc-green)]")
                                }
                                data-testid={`op-chip-${p.key}`}
                            >{p.label}</button>
                        );
                    })}
                </div>
            )}

            {/* Filters */}
            <div className="prc-card p-3 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="op-filters">
                <div>
                    <label className="prc-label-overline block mb-1">車両</label>
                    <select className="prc-input" value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)} data-testid="op-filter-vehicle">
                        <option value="">全車両</option>
                        {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate_number}</option>)}
                    </select>
                </div>
                <div>
                    <label className="prc-label-overline block mb-1">開始日</label>
                    <DatePickerYmd value={filterFrom} onChange={setFilterFrom} testid="op-filter-from" />
                </div>
                <div>
                    <label className="prc-label-overline block mb-1">終了日</label>
                    <DatePickerYmd value={filterTo} onChange={setFilterTo} testid="op-filter-to" />
                </div>
                <div>
                    <button
                        type="button"
                        className="prc-btn-ghost w-full"
                        onClick={() => { setFilterVehicle(""); setFilterFrom(""); setFilterTo(""); }}
                        data-testid="op-filter-clear"
                    >条件をクリア</button>
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2" data-testid="op-list-mobile">
                {list.length === 0 ? (
                    <p className="prc-card p-8 text-center text-sm text-[var(--prc-muted)]" data-testid="op-empty-mobile">
                        {isStaffView ? "あなたの運行記録はまだありません。「運行を記録」から登録してください。" : "該当する運行記録がありません"}
                    </p>
                ) : isStaffView && groupedList ? (
                    // Staff: grouped by date bucket with sticky header + color left border
                    groupedList.map((group) => (
                        <div key={group.key} className="space-y-2">
                            <div
                                className="sticky top-0 z-10 flex items-baseline gap-2 px-1 py-1 bg-[var(--prc-bg)]/95 backdrop-blur"
                                data-testid={`op-group-${group.key}`}
                            >
                                <span className="prc-label-overline !text-[var(--prc-green)]">// {group.key}</span>
                                <span className="text-[10px] font-mono text-[var(--prc-muted)]">{group.items.length} 件</span>
                            </div>
                            {group.items.map((o) => {
                                const k = kindLookup(o.kind);
                                return (
                                    <div
                                        key={o.id}
                                        className="prc-card op-item-scale p-3 active:scale-[0.99] transition"
                                        style={{ borderLeft: `4px solid ${k.color || "#555"}` }}
                                        data-testid={`op-card-${o.id}`}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <span className="font-mono text-xs">{fmtYmd(o.operation_date, o.operation_date)}</span>
                                            <span
                                                className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider"
                                                style={{
                                                    color: k.color,
                                                    borderColor: (k.color || "#888") + "66",
                                                    backgroundColor: (k.color || "#888") + "1a",
                                                }}
                                            >{k.label}</span>
                                        </div>
                                        <p className="text-sm font-semibold truncate">{o.vehicle_plate || "-"}</p>
                                        <p className="text-xs text-[var(--prc-muted)] truncate">
                                            {(o.start_time || "--:--")} – {(o.end_time || "--:--")}
                                        </p>
                                        <p className="text-xs mt-1 truncate">{o.origin || "—"} → {o.destination || "—"}</p>
                                        {o.purpose && <p className="text-[10px] text-[var(--prc-muted)] truncate">{o.purpose}</p>}
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--prc-border)]">
                                            <span className="text-xs font-mono">
                                                {o.distance_km != null ? `${o.distance_km} km` : "—"}
                                                {o.fuel_added_l != null && <span className="ml-2 text-[var(--prc-muted)]">{o.fuel_added_l} L</span>}
                                            </span>
                                            <div className="flex gap-1">
                                                {canUpdate && <IconButton icon={PencilSimple} size="sm" title="編集" onClick={() => openEdit(o)} data-testid={`op-edit-mobile-${o.id}`} />}
                                                {canDelete && <IconButton icon={Trash} size="sm" tone="danger" title="削除" onClick={() => remove(o.id)} data-testid={`op-del-mobile-${o.id}`} />}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                ) : (
                    // Admin / fallback flat list
                    list.map((o) => {
                        const k = kindLookup(o.kind);
                        return (
                            <div key={o.id} className="prc-card op-item-scale p-3 active:scale-[0.99] transition" data-testid={`op-card-${o.id}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="font-mono text-xs">{fmtYmd(o.operation_date, o.operation_date)}</span>
                                    <span
                                        className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider"
                                        style={{
                                            color: k.color,
                                            borderColor: (k.color || "#888") + "66",
                                            backgroundColor: (k.color || "#888") + "1a",
                                        }}
                                    >{k.label}</span>
                                </div>
                                <p className="text-sm font-semibold truncate">{o.vehicle_plate || "-"}</p>
                                <p className="text-xs text-[var(--prc-muted)] truncate">
                                    {(o.start_time || "--:--")} – {(o.end_time || "--:--")} · {o.driver_name || "-"}
                                </p>
                                <p className="text-xs mt-1 truncate">{o.origin || "—"} → {o.destination || "—"}</p>
                                {o.purpose && <p className="text-[10px] text-[var(--prc-muted)] truncate">{o.purpose}</p>}
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--prc-border)]">
                                    <span className="text-xs font-mono">
                                        {o.distance_km != null ? `${o.distance_km} km` : "—"}
                                        {o.fuel_added_l != null && <span className="ml-2 text-[var(--prc-muted)]">{o.fuel_added_l} L</span>}
                                    </span>
                                    <div className="flex gap-1">
                                        {canUpdate && <IconButton icon={PencilSimple} size="sm" title="編集" onClick={() => openEdit(o)} data-testid={`op-edit-mobile-${o.id}`} />}
                                        {canDelete && <IconButton icon={Trash} size="sm" tone="danger" title="削除" onClick={() => remove(o.id)} data-testid={`op-del-mobile-${o.id}`} />}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Desktop table */}
            <div className="prc-card overflow-x-auto hidden md:block">
                {list.length === 0 ? (
                    <p className="p-8 text-center text-sm text-[var(--prc-muted)]" data-testid="op-empty">
                        {isStaffView ? "あなたの運行記録はまだありません。「運行を記録」から登録してください。" : "該当する運行記録がありません"}
                    </p>
                ) : (
                    <table className="prc-table" data-testid="op-table">
                        <thead>
                            <tr>
                                <th>日付</th>
                                <th>時刻</th>
                                <th>車両</th>
                                {!isStaffView && <th>運転者</th>}
                                <th>区分</th>
                                <th>出発 → 到着</th>
                                <th className="text-right">距離</th>
                                <th className="text-right">給油</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody className="op-item-scale">
                            {list.map((o) => {
                                const rowKind = kindLookup(o.kind);
                                return (
                                <tr
                                    key={o.id}
                                    data-testid={`op-row-${o.id}`}
                                    style={isStaffView ? { boxShadow: `inset 4px 0 0 ${rowKind.color || "#555"}` } : undefined}
                                >
                                    <td className="num">{fmtYmd(o.operation_date, o.operation_date)}</td>
                                    <td className="num text-xs text-[var(--prc-muted)]">
                                        {(o.start_time || "--:--")} – {(o.end_time || "--:--")}
                                    </td>
                                    <td className="font-mono">{o.vehicle_plate || "-"}</td>
                                    {!isStaffView && <td>{o.driver_name || "-"}</td>}
                                    <td>
                                        {(() => {
                                            const k = kindLookup(o.kind);
                                            return (
                                                <span
                                                    className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider"
                                                    style={{
                                                        color: k.color,
                                                        borderColor: k.color + "66",
                                                        backgroundColor: k.color + "1a",
                                                    }}
                                                >
                                                    {k.label}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="max-w-xs truncate">
                                        {o.origin || "—"} → {o.destination || "—"}
                                        {o.purpose && <span className="block text-[10px] text-[var(--prc-muted)] truncate">{o.purpose}</span>}
                                    </td>
                                    <td className="num">{o.distance_km != null ? `${o.distance_km} km` : "-"}</td>
                                    <td className="num text-xs">{o.fuel_added_l != null ? `${o.fuel_added_l} L` : "-"}</td>
                                    <td>
                                        <div className="flex justify-end gap-1">
                                            {canUpdate && <IconButton icon={PencilSimple} size="sm" title="編集" onClick={() => openEdit(o)} data-testid={`op-edit-${o.id}`} />}
                                            {canDelete && <IconButton icon={Trash} size="sm" tone="danger" title="削除" onClick={() => remove(o.id)} data-testid={`op-del-${o.id}`} />}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4" data-testid="op-dialog">
                    {confirming ? (
                        <ConfirmPanel
                            form={form}
                            vehicles={vehicles}
                            kinds={kinds}
                            onBack={() => setConfirming(false)}
                            onConfirm={doSubmit}
                        />
                    ) : (
                    <form onSubmit={handleFormSubmit} className={`prc-card p-4 sm:p-6 w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-lg sm:rounded-sm ${isStaffView ? "op-form-scale" : ""}`}>
                        <p className="prc-label-overline mb-1 !text-[var(--prc-green)]">// 運行記録</p>
                        <h2 className="text-lg sm:text-xl font-bold mb-4">{form.id ? "運行記録を編集" : "運行を記録"}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <FieldRow label="車両 *" cols={2}>
                                {(() => {
                                    const v = vehicles.find((x) => x.id === form.vehicle_id);
                                    return (
                                        <div className="flex items-center gap-2">
                                            <div className="prc-input flex-1 !opacity-90 !cursor-default select-text" data-testid="op-form-vehicle-readonly">
                                                {v ? (
                                                    <span className="font-mono">
                                                        <span className="font-bold text-[var(--prc-ink)]">{v.plate_number}</span>
                                                        <span className="text-[var(--prc-muted)] ml-2">— {v.make} {v.model}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-[var(--prc-tertiary)]">QRスキャンで車両を選択してください</span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setQrScanOpen(true)}
                                                className="prc-btn-ghost inline-flex items-center gap-2 whitespace-nowrap"
                                                data-testid="op-form-vehicle-qr-btn"
                                            >
                                                <QrCode size={16} />再スキャン
                                            </button>
                                            {/* Hidden required-mirror so submit-time validation still fires */}
                                            <input
                                                type="text"
                                                required
                                                value={form.vehicle_id}
                                                readOnly
                                                onChange={() => {}}
                                                aria-hidden="true"
                                                tabIndex={-1}
                                                style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
                                            />
                                        </div>
                                    );
                                })()}
                            </FieldRow>
                            <FieldRow label="運行日 *">
                                <DatePickerYmd value={form.operation_date} onChange={(val) => setForm({ ...form, operation_date: val })} required testid="op-form-date" />
                            </FieldRow>
                            <FieldRow label="区分">
                                <select className="prc-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="op-form-kind">
                                    {enabledKinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                                </select>
                            </FieldRow>
                            <FieldRow label="開始時刻">
                                <input type="time" className="prc-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="op-form-start-time" />
                            </FieldRow>
                            <FieldRow label="終了時刻">
                                <input type="time" className="prc-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="op-form-end-time" />
                            </FieldRow>
                            <FieldRow label="出発地">
                                <select className="prc-input" value={form.origin || ""} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="op-form-origin">
                                    <option value="">選択してください</option>
                                    {enabledLocations.map((l) => <option key={l.value} value={l.label}>{l.label}</option>)}
                                    {/* Show the current value even if it's no longer in the list (legacy records) */}
                                    {form.origin && !enabledLocations.some((l) => l.label === form.origin) && (
                                        <option value={form.origin}>{form.origin}（廃止済）</option>
                                    )}
                                </select>
                                <p className="text-[10px] text-[var(--prc-muted)] mt-1 font-mono">// 候補は <a href="/settings" className="underline hover:text-[var(--prc-green)]">会社設定 → 運行地点マスタ</a> で管理</p>
                            </FieldRow>
                            <FieldRow label="到着地">
                                <select className="prc-input" value={form.destination || ""} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="op-form-destination">
                                    <option value="">選択してください</option>
                                    {enabledLocations.map((l) => <option key={l.value} value={l.label}>{l.label}</option>)}
                                    {form.destination && !enabledLocations.some((l) => l.label === form.destination) && (
                                        <option value={form.destination}>{form.destination}（廃止済）</option>
                                    )}
                                </select>
                            </FieldRow>
                            <FieldRow label="開始 オドメータ (km)">
                                <input type="number" className="prc-input" value={form.start_odometer_km} onChange={(e) => setForm({ ...form, start_odometer_km: e.target.value })} data-testid="op-form-start-odo" />
                                {!form.id && lastOdo?.last_end_odometer_km != null && (
                                    <p className="text-[10px] text-[var(--prc-muted)] mt-1 font-mono" data-testid="op-form-last-odo-hint">
                                        {String(form.start_odometer_km) === String(lastOdo.last_end_odometer_km) ? (
                                            <>// ✓ 前回終了オドメータを自動転記 ({lastOdo.last_end_odometer_km.toLocaleString()} km · {lastOdo.last_date || "—"})</>
                                        ) : (
                                            <>// 前回終了: {lastOdo.last_end_odometer_km.toLocaleString()} km ({lastOdo.last_date || "—"})</>
                                        )}
                                    </p>
                                )}
                            </FieldRow>
                            <FieldRow label="終了 オドメータ (km)">
                                <input type="number" className="prc-input" value={form.end_odometer_km} onChange={(e) => setForm({ ...form, end_odometer_km: e.target.value })} data-testid="op-form-end-odo" />
                            </FieldRow>
                            {previewDistance != null && (
                                <div className="sm:col-span-2 text-xs font-mono text-[var(--prc-green)] uppercase tracking-wider">
                                    // 走行距離: {previewDistance} km
                                </div>
                            )}
                            <FieldRow label="給油量 (L)">
                                <input type="number" step="0.01" className="prc-input" value={form.fuel_added_l} onChange={(e) => setForm({ ...form, fuel_added_l: e.target.value })} data-testid="op-form-fuel" />
                            </FieldRow>
                            <FieldRow label="乗車人数">
                                {(() => {
                                    // Cap dropdown options at the selected vehicle's seating capacity
                                    // (from 車検証 OCR). Falls back to 10 if unknown.
                                    const veh = vehicles.find((v) => v.id === form.vehicle_id);
                                    const cap = Math.max(1, Math.min(60, veh?.seating_capacity || 10));
                                    const cur = form.passenger_count;
                                    const numbers = Array.from({ length: cap }, (_, i) => i + 1);
                                    return (
                                        <select
                                            className="prc-input"
                                            value={cur ?? ""}
                                            onChange={(e) => setForm({ ...form, passenger_count: e.target.value })}
                                            data-testid="op-form-passengers"
                                        >
                                            <option value="">選択してください</option>
                                            {numbers.map((n) => (
                                                <option key={n} value={n}>{n} 名</option>
                                            ))}
                                            {/* Preserve a previously-saved value that exceeds the capacity */}
                                            {cur && !numbers.includes(parseInt(cur)) && (
                                                <option value={cur}>{cur} 名</option>
                                            )}
                                        </select>
                                    );
                                })()}
                                {form.vehicle_id && (() => {
                                    const veh = vehicles.find((v) => v.id === form.vehicle_id);
                                    return veh?.seating_capacity ? (
                                        <p className="text-[10px] text-[var(--prc-muted)] mt-1 font-mono">
                                            // 乗車定員: {veh.seating_capacity} 名
                                        </p>
                                    ) : null;
                                })()}
                            </FieldRow>
                            <FieldRow label="目的" cols={2}>
                                <input className="prc-input" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="定期配送 / 取引先打合せ など" data-testid="op-form-purpose" />
                            </FieldRow>
                            <FieldRow label="備考" cols={2}>
                                <textarea className="prc-input min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="op-form-notes" />
                            </FieldRow>
                        </div>
                        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--prc-border)]">
                            <button type="button" onClick={() => { setOpen(false); setConfirming(false); }} className="prc-btn-ghost" data-testid="op-form-cancel">キャンセル</button>
                            <button type="submit" className="prc-btn-primary" data-testid="op-form-submit">
                                {form.id ? "更新" : (isStaffView ? "確認へ進む" : "登録")}
                            </button>
                        </div>
                    </form>
                    )}
                </div>
            )}

            <QrScanner open={qrScanOpen} onClose={() => setQrScanOpen(false)} onDetect={handleQrScan} />
        </div>
    );
}

/**
 * Read-only confirmation panel shown to staff between "確認へ進む" and the
 * actual POST. Deliberately uses large type and generous spacing so that the
 * driver can sanity-check their entry at a glance on a phone.
 */
function ConfirmPanel({ form, vehicles, kinds, onBack, onConfirm }) {
    const [busy, setBusy] = useState(false);
    const veh = vehicles.find((v) => v.id === form.vehicle_id);
    const kind = kinds.find((k) => k.value === form.kind);
    const distance = (() => {
        const s = parseInt(form.start_odometer_km);
        const e = parseInt(form.end_odometer_km);
        if (Number.isFinite(s) && Number.isFinite(e) && e >= s) return e - s;
        return null;
    })();

    // Row component — denser on mobile (18px) to fit a 6" screen without
    // scroll, still large on desktop (24px).
    const Row = ({ label, value, accent, full }) => (
        <div className={`py-2 sm:py-3 border-b border-[var(--prc-border)] last:border-b-0 ${full ? "col-span-2" : ""}`}>
            <p className="prc-label-overline !text-[10px] sm:!text-xs mb-0.5 sm:mb-1">{label}</p>
            <p className={`text-lg sm:text-2xl font-semibold leading-tight sm:leading-snug break-words ${accent || ""}`} data-testid={`confirm-${label}`}>
                {value == null || value === "" ? <span className="text-[var(--prc-muted)]">—</span> : value}
            </p>
        </div>
    );

    const handleConfirm = async () => {
        setBusy(true);
        try { await onConfirm(); } finally { setBusy(false); }
    };

    return (
        <div className="prc-card p-3 sm:p-6 w-full sm:max-w-2xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto rounded-t-lg sm:rounded-sm" data-testid="op-confirm-panel">
            <p className="prc-label-overline mb-1 sm:mb-2 !text-amber-300 !text-[10px] sm:!text-xs">// 内容を確認してください</p>
            <h2 className="text-base sm:text-2xl font-bold mb-2 sm:mb-4">この内容で登録しますか？</h2>

            {/* 2-column grid on mobile for short fields; long fields span full width */}
            <div className="grid grid-cols-2 gap-x-3 sm:gap-x-4">
                <Row label="運行日" value={fmtYmd(form.operation_date, form.operation_date)} accent="text-[var(--prc-green)] font-mono" />
                <Row label="区分" value={kind?.label || form.kind} />
                <Row
                    label="車両"
                    full
                    value={veh ? `${veh.plate_number} — ${veh.make || ""} ${veh.model || ""}`.trim() : form.vehicle_id}
                />
                <Row label="開始" value={form.start_time || "--:--"} accent="font-mono" />
                <Row label="終了" value={form.end_time || "--:--"} accent="font-mono" />
                <Row
                    label="出発地 → 到着地"
                    full
                    value={`${form.origin || "—"} → ${form.destination || "—"}`}
                />
                {form.purpose && <Row label="目的" value={form.purpose} full />}
                <Row
                    label="オドメータ"
                    full
                    value={(form.start_odometer_km || "—") + " km → " + (form.end_odometer_km || "—") + " km"}
                    accent="font-mono"
                />
                {distance != null && (
                    <Row label="走行距離" value={`${distance.toLocaleString()} km`} accent="text-[var(--prc-green)] font-mono text-xl sm:text-2xl" />
                )}
                {form.fuel_added_l && <Row label="給油量" value={`${form.fuel_added_l} L`} accent="font-mono" />}
                {form.passenger_count && <Row label="乗車人数" value={`${form.passenger_count} 名`} />}
                {form.notes && <Row label="備考" value={form.notes} full />}
            </div>

            <div className="border border-amber-400/30 bg-amber-400/5 rounded-sm p-2 sm:p-3 mt-3 sm:mt-5 text-[11px] sm:text-xs text-amber-200 leading-snug">
                <span className="font-mono uppercase tracking-wider mr-2">// CHECK</span>
                誤りがあれば「戻って修正」を押してください。
            </div>

            <div className="flex flex-row justify-end gap-2 mt-3 sm:mt-5 pt-3 sm:pt-4 border-t border-[var(--prc-border)] sticky bottom-0 bg-[var(--prc-card)]/95 backdrop-blur">
                <button type="button" onClick={onBack} className="prc-btn-ghost flex-1 sm:flex-initial py-2.5 sm:py-2 text-sm" data-testid="op-confirm-back">戻って修正</button>
                <button type="button" onClick={handleConfirm} disabled={busy} className="prc-btn-primary flex-1 sm:flex-initial py-2.5 sm:py-2 text-sm sm:text-base font-bold disabled:opacity-50" data-testid="op-confirm-submit">
                    {busy ? "送信中..." : "✓ この内容で登録"}
                </button>
            </div>
        </div>
    );
}

function FieldRow({ label, cols = 1, children }) {
    return (
        <div className={cols === 2 ? "sm:col-span-2" : ""}>
            <label className="prc-label-overline block mb-1">{label}</label>
            {children}
        </div>
    );
}

function StatBox({ label, value, testId }) {
    return (
        <div className="prc-card p-4" data-testid={testId}>
            <p className="prc-label-overline">{label}</p>
            <p className="text-2xl font-bold mt-1 text-[var(--prc-green)] font-mono">{value}</p>
        </div>
    );
}
